import { Fragment, useEffect, useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { observer } from "mobx-react-lite";
import { useNavigate, useParams, useLocation, NavLink, Routes, Route } from "react-router-dom";
import _ from "lodash";
import DeleteIcon from "@mui/icons-material/Delete";
import { Popover, Transition } from "@headlessui/react";
import {
    ChatBubbleBottomCenterIcon,
    ChevronDownIcon,
    ClipboardDocumentCheckIcon,
    AdjustmentsHorizontalIcon,
    QuestionMarkCircleIcon,
    UserIcon,
    BuildingOfficeIcon,
    PaperClipIcon,
    PhotoIcon,
} from "@heroicons/react/24/solid";
import { toast } from "react-toastify";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import {
    Box,
    IconButton,
    Modal,
    Typography,
    Button,
    Switch,
    FormControlLabel,
    Card,
    CardContent,
    Grid,
    Dialog,
    DialogContent,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { DeleteModal, LoadingState, ProjectHeader } from "../components";
import Features from "../project-properties/Features";
import { safe_get } from "../report-visuals";
import QuestionsWindow from "../project-properties/QuestionsWindow";
import InfluentialLeaderWindow from "../project-properties/components/influenceLeaderProjectProperty/InfluenceLeaderWindow";
import {
    classNames,
    htmlValidation,
    makeid_alpa_numeric,
    renameKeys,
    useStore,
    useFileUpload,
    uploadBlob,
} from "../helpers";
import { FTPBreadCrumbs } from "../components-new";
import GovtConfig from "../project-properties/components/GovtConfig/govtConfigHome";
import PageShell from "../layouts/PageShell";
import { SurveyWebLinkManagement } from "../features/survey-weblink";
import ThumbnailImage from "../components-new/Thumbnail";
import { WebUxConfigBuilder } from "../project-properties/components/WebUxConfigBuilder";
import { AnalyticalSummary } from "../components/Analytical";
import AudioUpload from "../components-new/audioupload";
import ServiceRequestFormConfig from "../project-properties/components/ServiceRequestFormConfig";

// Main component to manage the project, wrapped in an observer to track MobX state changes.
const ManageProject = observer((props) => {
    const [t] = useTranslation("ManageProject");

    const rootStore = useStore();
    const { projectStore, influenceLeaderProjectProperty } = rootStore;
    const { questionStore, govtconfigStore } = rootStore;
    const navigate = useNavigate();
    const { projectid } = useParams();
    const { userStore } = rootStore;
    const isRootOrClient: boolean = userStore.isRoot() || userStore.isClient();
    const queryClient = useQueryClient();

    const [projectName, setProjectName] = useState("");
    const [disable, setDisable] = useState(false);
    const [surveyName, setSurveyName] = useState<{ surveyname?: string }>({});
    const [hide, setHide] = useState(true);
    const [loading, setLoading] = useState(false);
    const [fullProject, setFullProject] = useState({});
    const [allSurveyList, setAllSurveyList] = useState([]);
    const [surveys, setSurveys] = useState([]);
    const [selectedSurveyForCloning, setSelectedSurveyForCloning] = useState({});
    const [isSurveyListModalOpen, setIsSurveyListModalOpen] = useState(false);
    const [properties, setProperties] = useState({});
    const [features, setFeatures] = useState({});
    const [fetched, setFetched] = useState(false);
    const [activeProperty, setActiveProperty] = useState("empty-property");
    const [json, setJson] = useState("{}");
    const [isValidJson, setIsValidJson] = useState(false);
    const location = useLocation();

    type DeleteContext = {
        open: boolean;
        description?: string;
        onConfirm?: () => Promise<void> | void;
    };

    const [deleteContext, setDeleteContext] = useState<DeleteContext>({
        open: false,
    });

    const openDeleteModal = (config: Omit<DeleteContext, "open">) => {
        setDeleteContext({
            open: true,
            ...config,
        });
    };

    const closeDeleteModal = () => {
        setDeleteContext({ open: false });
    };

    // Map route paths to tab IDs
    const getTabIdFromPath = (pathname: string): number => {
        if (pathname.includes("/surveys")) return 1;
        if (pathname.includes("/properties")) return 2;
        if (pathname.includes("/web-features")) return 3;
        if (pathname.includes("/profile-questions")) return 4;
        if (pathname.includes("/influential-leaders")) return 5;
        if (pathname.includes("/voter-segmentation-config")) return 6;
        if (pathname.includes("/web-survey-links")) return 7;
        if (pathname.includes("/banners")) return 8;
        if (pathname.includes("/webuxconfig")) return 9;
        if (pathname.includes("/analytical")) return 10;
        if (pathname.includes("/audio")) return 11;
        return 1; // default to surveys
    };

    const activeTabName = getTabIdFromPath(location.pathname);
    const [popOver, setPopOver] = useState(false);
    const [leader, setLeader] = useState(false);

    // Define tabs for navigation within the project management interface.
    const tabs = [
        {
            id: 1,
            name: t("surveys"),
            icon: ClipboardDocumentCheckIcon,
            path: "surveys",
        },
        {
            id: 2,
            name: t("properties"),
            icon: ChatBubbleBottomCenterIcon,
            path: "properties",
        },
        {
            id: 3,
            name: t("web_features"),
            icon: AdjustmentsHorizontalIcon,
            path: "web-features",
        },
        {
            id: 4,
            name: "Profile Questions",
            icon: QuestionMarkCircleIcon,
            path: "profile-questions",
        },
        ...(leader
            ? [
                  {
                      id: 5,
                      name: "Influential Leaders",
                      icon: UserIcon,
                      path: "influential-leaders",
                  },
              ]
            : []),
        {
            id: 6,
            name: "Voter Segmentation Config",
            icon: BuildingOfficeIcon,
            path: "voter-segmentation-config",
        },
        {
            id: 7,
            name: "Web Survey Links",
            icon: PaperClipIcon,
            path: "web-survey-links",
        },
        { id: 8, name: t("banners"), icon: PhotoIcon, path: "banners" },
        {
            id: 9,
            name: "Web UX Config",
            icon: AdjustmentsHorizontalIcon,
            path: "webuxconfig",
        },
        {
            id: 10,
            name: "Analytical Summary",
            icon: AdjustmentsHorizontalIcon,
            path: "analytical",
        },
        {
            id: 11,
            name: "Audios Upload",
            icon: ChatBubbleBottomCenterIcon,
            path: "audio",
        },
    ];

    // Function to handle the change of project name selection.
    const handleChange = (event: SelectChangeEvent) => {
        setProjectName(event.target.value);
        setHide(false);
        setDisable(true);
    };

    // Function to handle the change of survey name selection.
    // const handleChangeId = (event: SelectChangeEvent) => {
    //   setSurveyName(event.target.value);
    //   setSelectedSurveyForCloning(event.target.value);
    // };

    // Breadcrumbs configuration for navigation.
    const crumbs = [
        { name: "DashBoard", href: `/project/${projectid}`, current: false },
        {
            name: "Manage Project",
            href: `/project/${projectid}/manage/surveys`,
            current: true,
        },
    ];

    // Function to fetch project data and set state accordingly.
    const fetchProjectData = async () => {
        setLoading(true);
        const response = await projectStore.getFullProject(projectid);

        const defaultData = {
            name: "test",
            description: "test Desc",
            dates: "2021",
        };
        setFullProject(response.data == null ? defaultData : response.data);
        setSurveys(response.data["surveys"]);
        setLoading(false);
        setProperties(response.data["properties"]);
        setFetched(true);
        setFeatures(properties["features"]);
        const hasInfluenLeader = Object.keys(response.data["properties"]).includes("influential_leaders");
        setLeader(hasInfluenLeader);
    };

    // Function to fetch the list of all surveys.
    const fetchAllSurveyListData = async () => {
        setLoading(true);
        const response = await projectStore.getAllSurveyList();
        setAllSurveyList(response);
        setLoading(false);
    };

    // useEffect hook to load project and survey data on component mount or when project ID changes.
    useEffect(() => {
        fetchProjectData();
        fetchAllSurveyListData();
        setActiveProperty("empty-property");
    }, [projectid]);

    // Redirect to default tab (surveys) if on base /manage path
    useEffect(() => {
        if (
            location.pathname === `/project/${projectid}/manage` ||
            location.pathname === `/project/${projectid}/manage/`
        ) {
            navigate(`/project/${projectid}/manage/surveys`, { replace: true });
        }
    }, [location.pathname, projectid, navigate]);

    // Function to clone a new survey based on the selected survey.
    // const newSurveyClone = async () => {
    //   if (!Object.keys(selectedSurveyForCloning).includes("surveyid")) return;
    //   setLoading(true);
    //   await projectStore.cloneSurvey(
    //     fullProject["id"],
    //     selectedSurveyForCloning["surveyid"],
    //   );
    //   await fetchProjectData();
    //   setProjectName("");
    //   setSurveyName({});
    //   setLoading(false);
    // };

    const newSurveyClone = async () => {
        console.log("Selected Survey for Cloning:", selectedSurveyForCloning);

        // Debugging step
        if (!selectedSurveyForCloning?.["surveyid"]) {
            console.error("Error: selectedSurveyForCloning does not contain surveyid");
            return;
        }

        setLoading(true);
        await projectStore.cloneSurvey(fullProject["id"], selectedSurveyForCloning["surveyid"]);
        await fetchProjectData();
        setProjectName("");
        setSurveyName({});
        setLoading(false);
    };

    // Function to create a new blank survey.
    const newBlankSurvey = async () => {
        setLoading(true);
        await projectStore.blankSurvey(fullProject["id"]);
        await fetchProjectData();
        setLoading(false);
        toast.success("Blank Survey created successfully");
    };

    // Function to delete an existing survey.
    const deleteSurvey = async (surveyIdForDeletion) => {
        setLoading(true);
        await projectStore.deleteSurvey(fullProject["id"], surveyIdForDeletion);
        await fetchProjectData();
        setLoading(false);
    };

    // Component to display project metadata.
    const ProjectMeta = () => {
        return (
            <div>
                <div className="text-black-500 text-2xl text-base font-bold">{fullProject["name"]}</div>
                <div className="text-base">{fullProject["dates"]}</div>
                <div className="text-sm">{fullProject["description"]}</div>
            </div>
        );
    };

    // Component to render survey editor links.
    const SurveyEditorLinks = () => {
        const header = (
            <div className="flex flex-row items-center">
                <h3 className="title font-bold">{t("manage_surveys")}</h3>
                <IconButton
                    aria-label="delete"
                    onClick={() => {
                        setIsSurveyListModalOpen(true);
                    }}
                    sx={{ width: "fit-content" }}
                >
                    <i className="fa-solid fa-plus-circle" />
                </IconButton>
            </div>
        );

        if (!surveys) {
            return <div className="my-2">{header}</div>;
        }

        return (
            <div className="my-2">
                {header}
                <ul className={"my-2"}>
                    {surveys.map((x) => {
                        return (
                            <li
                                key={x["id"]}
                                className="mx-4 my-2 grid max-w-none grid-cols-3 gap-5 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7"
                            >
                                <div className="">Survey ID: {x["id"]}</div>
                                <div
                                    className="cursor-pointer font-bold text-blue-700"
                                    onClick={() => {
                                        navigate(`/project/${projectid}/manage/survey/${x["id"]}/edit`);
                                    }}
                                >
                                    {x["name"]}
                                </div>
                                <IconButton
                                    color="error"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        htmlValidation(e);

                                        openDeleteModal({
                                            description: "Do you want to delete this survey?",
                                            onConfirm: async () => {
                                                await deleteSurvey(x.id);
                                                closeDeleteModal();
                                            },
                                        });
                                    }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </li>
                        );
                    })}
                </ul>
            </div>
        );
    };

    // Function to close the survey list modal and reset states.
    const closeSurveyListModalOpen = () => {
        setIsSurveyListModalOpen(false);
        setProjectName("");
        setSurveyName({});
        setDisable(false);
    };

    // Component to render the modal dialog for selecting and cloning surveys.
    const ModalDialog = () => {
        const allSurveyListByProject = _.groupBy(allSurveyList, (x) => {
            return x["projectname"];
        });
        const handleChangeId = (event: SelectChangeEvent) => {
            const selectedSurvey = allSurveyListByProject[projectName].find(
                (x) => x.surveyname === event.target.value
            );

            if (selectedSurvey) {
                setSurveyName(selectedSurvey);
                setSelectedSurveyForCloning(selectedSurvey);
            }
        };

        const style = {
            position: "absolute" as "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            border: "1px solid #000",
            boxShadow: 24,
            p: 4,
        };

        return (
            <Modal
                open={isSurveyListModalOpen}
                onClose={closeSurveyListModalOpen}
                sx={{
                    display: "flex",
                    p: 1,
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Box sx={style}>
                    <div className="flex flex-col overflow-y-hidden">
                        <div className="flex flex-row flex-nowrap">
                            <div className="text-2xl font-bold">{t("add_new_survey")}</div>
                            <div
                                className="ml-auto cursor-pointer text-2xl text-red-500 hover:text-red-800"
                                onClick={closeSurveyListModalOpen}
                            >
                                <i className="fa-solid fa-times" />
                            </div>
                        </div>

                        <div className="text-md italic">{t("existing_survey_to_clone_from")}</div>

                        <div className="flex-1 overflow-y-auto">
                            <div className="d-flex flex-column pt-3 text-left sm:grid sm:grid-cols-1 sm:items-start">
                                <FormControl sx={{ m: 1, minWidth: 300 }}>
                                    <label
                                        htmlFor="message"
                                        className="block rounded-lg text-left font-semibold text-gray-700"
                                    >
                                        {t("project_name")}
                                    </label>

                                    <Select
                                        labelId="project_name"
                                        id="ProjectName"
                                        value={projectName}
                                        onChange={handleChange}
                                        className=""
                                    >
                                        {Object.keys(allSurveyListByProject).map((projectName) => {
                                            return (
                                                <MenuItem key={projectName} value={projectName}>
                                                    {" "}
                                                    {projectName}{" "}
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>

                                <div className="d-flex flex-column pt-3 text-left sm:grid sm:grid-cols-1 sm:items-start">
                                    <FormControl sx={{ m: 1, minWidth: 300 }}>
                                        <label
                                            htmlFor="message"
                                            className="block rounded-lg text-left font-semibold text-gray-700"
                                        >
                                            {t("survey_name")}
                                        </label>

                                        <Select
                                            labelId="Survey_name"
                                            id="SurveyName"
                                            value={surveyName?.surveyname || ""}
                                            onChange={handleChangeId}
                                            disabled={hide}
                                        >
                                            {allSurveyListByProject[projectName]?.map((x, index) => (
                                                <MenuItem key={index} value={x.surveyname}>
                                                    {x.surveyname}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </div>
                            </div>
                        </div>

                        <div className="mt-8 flex flex-row-reverse flex-nowrap">
                            <button
                                onClick={async () => {
                                    await newSurveyClone();
                                    setDisable(false);
                                    setIsSurveyListModalOpen(false);
                                }}
                                className="block flex rounded-full border-2 bg-green-600 px-4 py-2 text-center text-white shadow"
                            >
                                {t("clone_selected_survey")}
                            </button>
                            <button
                                disabled={disable}
                                onClick={async () => {
                                    await newBlankSurvey();
                                    setIsSurveyListModalOpen(false);
                                }}
                                className={
                                    disable
                                        ? "mx-4 block flex rounded-full border-2 bg-blue-300 px-4 py-2 text-center text-white shadow"
                                        : "mx-4 block flex rounded-full border-2 bg-blue-600 px-4 py-2 text-center text-white shadow"
                                }
                            >
                                {t("create_blank_survey")}
                            </button>
                            <button
                                onClick={() => {
                                    setIsSurveyListModalOpen(false);
                                    setProjectName("");
                                    setSurveyName({});
                                    setHide(true);
                                    setDisable(false);
                                }}
                                className="block flex rounded-full border-2 border-red-600 px-4 py-2 text-center text-red-600 shadow"
                            >
                                {t("cancel")}
                            </button>
                        </div>
                    </div>
                </Box>
            </Modal>
        );
    };

    const TabView = () => {
        return (
            <div className="">
                <div className="sm:hidden">
                    <label htmlFor="tabs" className="sr-only">
                        {t("select_a_tab")}
                    </label>
                    <select
                        id="tabs"
                        name="tabs"
                        className="block w-full rounded-lg border-gray-300 p-4 focus:border-indigo-500 focus:ring-indigo-500"
                        value={activeTabName}
                        onChange={(eve) => {
                            const selectedTab = tabs.find((t) => t.id === parseInt(eve.target.value));
                            if (selectedTab) {
                                navigate(`/project/${projectid}/manage/${selectedTab.path}`);
                            }
                        }}
                    >
                        {tabs.map((tab) => {
                            return (
                                <option key={tab.id} value={tab.id}>
                                    {tab.name}
                                </option>
                            );
                        })}
                    </select>
                </div>
                {/*<div className="hidden sm:block">*/}
                {/*    <div className="border-b-2 border-gray-400">*/}
                {/*        <nav*/}
                {/*            className="-mb-px flex space-x-8"*/}
                {/*            aria-label="Tabs"*/}
                {/*        >*/}
                <div className="hidden sm:block">
                    <div className="relative border-b-2 border-gray-400">
                        <nav
                            className="no-scrollbar -mb-px flex space-x-6 overflow-x-auto scroll-smooth whitespace-nowrap"
                            aria-label="Tabs"
                        >
                            {tabs.map((tab) => {
                                const tabPath = `/project/${projectid}/manage/${tab.path}`;
                                return (
                                    <NavLink
                                        key={tab.name}
                                        to={tabPath}
                                        className={({ isActive }) =>
                                            classNames(
                                                isActive
                                                    ? "border-indigo-500 text-indigo-600"
                                                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700",
                                                "group inline-flex cursor-pointer items-center border-b-2 px-1 py-2"
                                            )
                                        }
                                    >
                                        {({ isActive }) => (
                                            <>
                                                <tab.icon
                                                    className={classNames(
                                                        isActive
                                                            ? "text-indigo-500"
                                                            : "text-gray-400 group-hover:text-gray-500",
                                                        "-ml-0.5 mr-2 h-8 w-8"
                                                    )}
                                                    aria-hidden="true"
                                                />
                                                <span className="text-lg font-medium">{tab.name}</span>
                                            </>
                                        )}
                                    </NavLink>
                                );
                            })}
                        </nav>
                        <div className="pointer-events-none absolute right-0 top-0 h-full w-12 bg-gradient-to-l from-white to-transparent" />
                    </div>
                </div>
            </div>
        );
    };

    const ActiveProperties = () => {
        const properties = fullProject["properties"];
        return (
            <div>
                {Object.keys(fullProject["properties"])
                    .sort()
                    .map((name, index) => {
                        const isActive = name === activeProperty;
                        // console.log(name);
                        return (
                            <div
                                key={index}
                                className={classNames(
                                    isActive
                                        ? "rounded-lg bg-white font-bold text-blue-600"
                                        : "text-gray-800",
                                    "my-2 cursor-pointer px-4 py-2 font-medium uppercase"
                                )}
                                onClick={(evt) => {
                                    setActiveProperty(name);
                                    setJson(
                                        JSON.stringify(fullProject["properties"][name]["value"], null, 4)
                                    );
                                }}
                            >
                                {properties[name]["type"] === "json" && (
                                    <i className="fa-solid fa-code px-2" />
                                )}
                                {properties[name]["type"] === "value" && (
                                    <i className="fa-solid fa-paperclip px-2" />
                                )}
                                {name}
                            </div>
                        );
                    })}
            </div>
        );
    };

    const addJson = () => {
        const new_ds_id = makeid_alpa_numeric(5);
        const new_property = _.clone(fullProject["properties"]);

        const new_ds = {
            id: new_ds_id,
            type: "json",
            value: {
                name: t("test"),
            },
        };
        new_property[new_ds_id] = new_ds;
        setFullProject({ ...fullProject, properties: new_property });
        setPopOver(false);
    };

    // Function to add a new value property to the project.
    const addValue = () => {
        const new_ds_id = makeid_alpa_numeric(5);
        const new_valueproperty = _.clone(fullProject["properties"]);

        const new_ds = {
            id: new_ds_id,
            type: "value",
            value: "value",
        };

        new_valueproperty[new_ds_id] = new_ds;
        setFullProject({ ...fullProject, properties: new_valueproperty });
        setPopOver(false);
    };

    const saveProjectProperties = async (isValidJson) => {
        const payload = {
            id: fullProject["id"],
            properties: fullProject["properties"],
        };
        setLoading(true);

        if (!isValidJson) {
            setLoading(false);
            return;
        }

        await projectStore.editProjectProperties(payload);
        fetchProjectData();
        setLoading(false);
        setIsValidJson(false);
    };

    const saveEditedJSON = () => {
        try {
            const data = JSON.parse(json);
            setIsValidJson(true);
            let cloned = _.clone(fullProject["properties"]);
            cloned[activeProperty]["value"] = data;
            setFullProject({ ...fullProject, properties: cloned });

            saveProjectProperties(!isValidJson);

            toast.success("Property Updated Successfully");
        } catch (e) {
            toast.error("invalid JSON");
            setIsValidJson(false);
        }
    };

    const PropertiesLeftSection = () => {
        const datasetTypes = [
            { name: "json", description: "upload json data", key: "json" },
            { name: "value", description: "value", key: "value" },
        ];

        return (
            <>
                <div className="flex flex-row">
                    <div className="font-semibold uppercase">{t("proper")}</div>
                    <div className="ml-auto">
                        <Popover className="relative">
                            {({ open }) => (
                                <>
                                    <Popover.Button
                                        onClick={() => setPopOver(true)}
                                        className={classNames(
                                            open ? "text-gray-900" : "text-gray-500",
                                            "group mx-4 inline-flex items-center rounded-md bg-white px-4 py-2 text-base font-medium hover:text-gray-900"
                                        )}
                                    >
                                        <span>{t("add_new_property")}</span>
                                        <ChevronDownIcon
                                            className={classNames(
                                                open ? "text-gray-600" : "text-gray-400",
                                                "ml-2 h-5 w-5 group-hover:text-gray-500"
                                            )}
                                            aria-hidden="true"
                                        />
                                    </Popover.Button>

                                    <Transition
                                        show={popOver}
                                        as={Fragment}
                                        enter="transition ease-out duration-200"
                                        enterFrom="opacity-0 translate-y-1"
                                        enterTo="opacity-100 translate-y-0"
                                        leave="transition ease-in duration-150"
                                        leaveFrom="opacity-100 translate-y-0"
                                        leaveTo="opacity-0 translate-y-1"
                                    >
                                        <Popover.Panel
                                            static
                                            className="absolute left-1/2 z-10 mt-3 w-screen max-w-xs -translate-x-1/2 transform px-2 sm:px-0"
                                        >
                                            <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                                                <div className="relative grid gap-6 bg-white px-3 py-2 sm:gap-8 sm:p-3">
                                                    <button
                                                        onClick={() => {
                                                            console.log("Closing Popover");
                                                            setPopOver(false);
                                                        }}
                                                        className="absolute right-2 top-2 cursor-pointer p-2"
                                                    >
                                                        <span className="text-lg font-bold text-gray-500">
                                                            X
                                                        </span>
                                                    </button>
                                                    {datasetTypes.map((item) => (
                                                        <div
                                                            key={item.name}
                                                            onMouseDown={(evt) => {
                                                                console.log("Adding section ", item.key);

                                                                if (item.key === "json") addJson();
                                                                if (item.key === "value") addValue();
                                                            }}
                                                            className="-m-3 block cursor-pointer rounded-sm p-3 transition duration-150 ease-in-out hover:bg-blue-50"
                                                        >
                                                            <p className="text-base font-bold text-gray-900">
                                                                {item.name}
                                                            </p>
                                                            <p className="text-sm text-gray-500">
                                                                {item.description}
                                                            </p>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </Popover.Panel>
                                    </Transition>
                                </>
                            )}
                        </Popover>
                    </div>
                </div>
                {ActiveProperties()}
            </>
        );
    };

    // Function to delete the currently active property.
    const deleteActiveProperty = () => {
        const hasActiveProperty = activeProperty !== "empty-property";
        if (!hasActiveProperty) return;
        const fullProjectCloned = _.clone(fullProject);

        delete fullProjectCloned["properties"][activeProperty];
        setFullProject(fullProjectCloned);
        setActiveProperty("empty-property");
    };

    // Component to render the main section where project properties are displayed and edited.
    const RenderProperties = () => {
        const hasActiveProperty = activeProperty !== "empty-property";

        // Function to render the dedicated Service Request Form configuration UI.
        const RenderServiceRequestFormConfig = () => {
            const serviceRequestProperty = fullProject?.["properties"]?.["service_request_form"];

            if (!serviceRequestProperty) {
                return <div className="p-6 text-gray-500">Service Request Form property not found.</div>;
            }

            const handleSave = async (updatedConfig) => {
                const updatedProject = _.cloneDeep(fullProject);

                updatedProject["properties"]["service_request_form"] = {
                    ...updatedProject["properties"]["service_request_form"],
                    value: updatedConfig,
                };

                const payload = {
                    id: updatedProject["id"],
                    properties: updatedProject["properties"],
                };

                try {
                    setLoading(true);
                    await projectStore.editProjectProperties(payload);
                    setFullProject(updatedProject);

                    // Refresh manage-project UI. getFullProject also dehydrates,
                    // but the API response can be briefly stale and overwrite
                    // the config we just saved — so re-apply saved value after.
                    await fetchProjectData();

                    await projectStore.syncActiveProjectPropertyValue(
                        "service_request_form",
                        updatedConfig,
                        updatedProject["id"]
                    );

                    toast.success("Service Request Form configuration saved successfully");
                } catch (error) {
                    console.error("Error saving Service Request Form configuration:", error);
                    toast.error("Failed to save Service Request Form configuration");
                    throw error;
                } finally {
                    setLoading(false);
                }
            };

            return <ServiceRequestFormConfig value={serviceRequestProperty["value"]} onSave={handleSave} />;
        };

        // Function to render the currently active property.
        const RenderActiveProperty = () => {
            if (!fullProject["properties"][activeProperty]) return null;

            const { type, value } = fullProject["properties"][activeProperty];

            if (activeProperty === "service_request_form") {
                return RenderServiceRequestFormConfig();
            }

            return (
                <>
                    <div className="no-wrap flex flex-row items-center">
                        <div className="h-12 w-full border-b-2 border-gray-200 px-4 py-2 text-lg font-bold">
                            {activeProperty}
                        </div>
                        <div className="ml-auto">
                            <div
                                className="font-2xl mr-2 cursor-pointer text-red-500"
                                title="Delete section"
                                onClick={() => {
                                    openDeleteModal({
                                        description: `Do you want to delete property "${activeProperty}"?`,
                                        onConfirm: () => {
                                            deleteActiveProperty();
                                            closeDeleteModal();
                                        },
                                    });
                                }}
                            >
                                <i className="fa-solid fa-trash" />
                            </div>
                        </div>
                    </div>

                    <div className="h-full">
                        <div className="px-4 py-2">
                            <div className="mt-1">
                                <label className="block text-sm font-bold font-medium text-gray-700">
                                    {t("section_name")}
                                </label>
                                <div className="mt-1 flex rounded-md shadow-sm">
                                    <input
                                        type="text"
                                        value={activeProperty}
                                        onChange={(event) => {
                                            const oldPropertyName = activeProperty;
                                            const newPropertyName = event.target.value;
                                            let changed = _.clone(fullProject["properties"]);
                                            changed = renameKeys(changed, oldPropertyName, newPropertyName);
                                            setFullProject({
                                                ...fullProject,
                                                properties: changed,
                                            });
                                            setActiveProperty(newPropertyName);
                                        }}
                                        className="block h-8 w-full flex-1 rounded-md rounded-none rounded-r-md border-gray-300 px-2 py-2 shadow-md focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                        placeholder="Section name..."
                                    />
                                </div>
                            </div>
                            <div className="mt-1">
                                <label className="block text-sm font-bold font-medium text-gray-700">
                                    {t("property_type")}
                                </label>
                                <div className="mt-1 flex rounded-md shadow-sm">
                                    <input
                                        type="text"
                                        value={type}
                                        onChange={(event) => {}}
                                        className="block h-8 w-full flex-1 rounded-md rounded-none rounded-r-md border-gray-300 px-2 py-2 shadow-md focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                        placeholder="Section name..."
                                    />
                                </div>
                            </div>
                        </div>
                        {type === "json" ? RenderActiveReport_json() : null}
                        {type === "value" ? RenderActiveReport_value() : null}
                    </div>
                </>
            );
        };

        // Function to render JSON property.
        const RenderActiveReport_json = () => {
            if (!hasActiveProperty) return null;
            return (
                <div className="mt-5">
                    <label className="ml-4 block text-sm font-bold font-medium text-gray-700">
                        {t("property_settings")}
                    </label>
                    <textarea
                        className="mx-4 w-full rounded-lg bg-gray-50 bg-white px-4 py-2"
                        value={json}
                        onPaste={(evt) => {
                            const content = evt.clipboardData.getData("text/plain");
                            try {
                                const updated = JSON.parse(content);
                                let cloned = _.clone(fullProject["properties"]);
                                cloned[activeProperty]["value"] = updated;
                                setFullProject({
                                    ...fullProject,
                                    properties: cloned,
                                });
                            } catch (e) {
                                // console.log(e)
                            }
                        }}
                        onChange={(evt) => {
                            try {
                                const val = evt.target.value;
                                setJson(val);
                            } catch (e) {
                                console.log(e);
                            }
                        }}
                        style={{ height: "90vh" }}
                    />
                </div>
            );
        };

        // Function to render value property.
        const RenderActiveReport_value = () => {
            if (!hasActiveProperty) return null;
            const { type, value } = fullProject["properties"][activeProperty];

            return (
                <>
                    <div className="px-4 py-2">
                        <div className="mt-5">
                            <label className="block text-sm font-bold font-medium text-gray-700">
                                {t("property_settings")}
                            </label>
                            <div className="mt-1 flex rounded-md shadow-sm">
                                <input
                                    type="text"
                                    value={value}
                                    onChange={(event) => {
                                        const updated = event.target.value;
                                        const clonedProperties = _.clone(fullProject["properties"]);
                                        clonedProperties[activeProperty]["value"] = updated;
                                        setFullProject({
                                            ...fullProject,
                                            properties: clonedProperties,
                                        });
                                    }}
                                    className="block h-8 w-full flex-1 rounded-md rounded-none rounded-r-md border-gray-300 px-2 py-2 shadow-md focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                    placeholder="Section name..."
                                />
                            </div>
                        </div>
                    </div>
                </>
            );
        };

        // Function to display placeholder text when no property is selected.
        const propertyPlaceHolderText = () => {
            return (
                <div className="px-8 py-8 text-lg italic text-gray-500">{t("select_project_property")}</div>
            );
        };

        const activeType = fullProject["properties"][activeProperty];

        return (
            <div>
                <div className="flex flex-row border-b-2 border-t-2 border-gray-300">
                    <div className="text-black-500 w-full border-r-2 border-gray-300 p-4 text-2xl text-base font-bold md:w-1/3 lg:w-1/4">
                        {t("project_properties")}
                    </div>
                    <div className="ml-auto self-center">
                        {activeProperty !== "service_request_form" && (
                            <button
                                className="rounded-md bg-indigo-700 px-4 py-2 text-white"
                                onClick={() => {
                                    if (activeProperty === "service_request_form") return;

                                    activeType && activeType.type != "value"
                                        ? saveEditedJSON()
                                        : saveProjectProperties(true);
                                }}
                            >
                                {t("save")}
                            </button>
                        )}
                    </div>
                </div>
                <div
                    className="flex w-full flex-col flex-wrap overflow-hidden md:flex-row"
                    key="content-left"
                >
                    <div className="w-full border-r-2 border-gray-300 py-2 md:w-1/3 lg:w-1/4">
                        {PropertiesLeftSection()}
                    </div>
                    <div className="w-full md:w-2/3 lg:w-3/4" key="content-right">
                        {hasActiveProperty ? RenderActiveProperty() : propertyPlaceHolderText()}
                    </div>
                </div>
            </div>
        );
    };

    let web_properties = safe_get(fullProject, "properties.web_features.value", {});

    let app_properties = safe_get(fullProject, "properties.features.value", {});

    const WebFeatures = () => {
        // Function to handle the saving of updated settings
        async function handleSave(settings: object, title: string) {
            // Clone the fullProject object to avoid mutating the original state directly
            const updatedSettings = _.cloneDeep(fullProject);

            // Check if the title corresponds to "Web Features"
            if (title === "Web Features") {
                // Merge the existing web_properties with the new settings
                web_properties = { ...web_properties, ...settings };

                // Update the 'web_features' in the cloned fullProject object
                _.set(updatedSettings, "properties.web_features.value", web_properties);

                // Check if the title corresponds to "App Features"
            } else if (title === "App Features") {
                // Merge the existing app_properties with the new settings
                app_properties = { ...app_properties, ...settings };

                // Update the 'features' in the cloned fullProject object
                _.set(updatedSettings, "properties.features.value", app_properties);
            }

            const payload = {
                id: updatedSettings["id"],
                properties: updatedSettings["properties"],
            };

            setLoading(true);
            await projectStore.editProjectProperties(payload);
            await fetchProjectData();
            setLoading(false);

            // Update the state of fullProject with the modified settings
            setFullProject({ ...updatedSettings });
        }

        return (
            <>
                <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-1 md:grid-cols-1">
                    <Features json={web_properties} title={"Web Features"} onSave={handleSave} />

                    {/* Features component for handling App Features */}
                    <Features json={app_properties} title={"App Features"} onSave={handleSave} />
                </div>
            </>
        );
    };

    useEffect(() => {
        const ls_fullProject = JSON.parse(localStorage.getItem("__active_project"));
        const _IL = safe_get(ls_fullProject, "properties.influential_leaders.value", {});

        const _PQ = safe_get(ls_fullProject, "properties.profile_questions.value", {});

        const _GC = safe_get(ls_fullProject, "properties.gotv_config.value", {});

        if (!_.isEmpty(_PQ)) {
            questionStore.initializeStore(_PQ);
        }
        if (!_.isEmpty(_IL)) {
            influenceLeaderProjectProperty.initializeStore(_IL);
        }
        if (!_.isEmpty(_GC)) {
            govtconfigStore.initializeStore(_GC);
        }
    }, []);

    const ProfileQuestions = () => {
        async function onSave(questions) {
            console.log(questions);
            const updatedValues = _.cloneDeep(fullProject);
            _.set(updatedValues, "properties.profile_questions.value", questions);
            try {
                const payload = {
                    id: updatedValues["id"],
                    properties: updatedValues["properties"],
                };
                setLoading(true);
                await projectStore.editProjectProperties(payload);
                await fetchProjectData();
                setLoading(false);

                // // Update the state of fullProject with the modified settings
                setFullProject({ ...updatedValues });
            } catch (error) {
                console.log(error);
                alert("Server down...");
            }
        }
        return (
            <>
                <QuestionsWindow onSave={onSave} />
            </>
        );
    };

    const InfluentialLeaders = () => {
        async function onSave(leaders) {
            const updatedValues = _.cloneDeep(fullProject);
            _.set(updatedValues, "properties.influential_leaders.value", leaders);
            const payload = {
                id: updatedValues["id"],
                properties: updatedValues["properties"],
            };

            setLoading(true);
            await projectStore.editProjectProperties(payload);
            await fetchProjectData();
            setLoading(false);

            // // Update the state of fullProject with the modified settings
            setFullProject({ ...updatedValues });
            console.log(updatedValues);
        }
        return (
            <>
                <InfluentialLeaderWindow onSave={onSave} />
            </>
        );
    };

    const Gotv = () => {
        async function onSave(gotv: any) {
            const updatedValues = _.cloneDeep(fullProject);
            _.set(updatedValues, "properties.gotv_config.value", gotv);
            console.log("Updated GOTV config:", updatedValues["properties"].gotv_config.value);
            govtconfigStore.initializeStore(updatedValues["properties"].gotv_config.value);

            try {
                const payload = {
                    id: updatedValues["id"],
                    properties: updatedValues["properties"],
                };

                setLoading(true);
                await projectStore.editProjectProperties(payload);
                await fetchProjectData();
                setLoading(false);

                // ✅ Update state with latest project data
                setFullProject({ ...updatedValues });
            } catch (error) {
                console.error("Error updating GOTV:", error);
                alert("Server down...");
                setLoading(false);
            }
        }
        // @ts-ignore
        // @ts-ignore

        return (
            <>
                {/*<GovtConfig onSave={onSave} />*/}
                {/*<GovtConfig*/}
                {/*    surveys={surveys}*/}
                {/*    initialValue={safe_get(*/}
                {/*        fullProject,*/}
                {/*        "properties.gotv_config.value",*/}
                {/*        null*/}
                {/*    )}*/}
                {/*    onSave={async (config) => {*/}
                {/*        const updated = _.cloneDeep(fullProject);*/}
                {/*        _.set(updated, "properties.gotv_config", {*/}
                {/*            id:*/}
                {/*                // @ts-ignore*/}
                {/*                updated.properties.gotv_config?.id ||*/}
                {/*                makeid_alpa_numeric(5),*/}
                {/*            type: "json",*/}
                {/*            value: config,*/}
                {/*        });*/}
                {/*        // @ts-ignore*/}
                {/*        await projectStore.editProjectProperties({*/}
                {/*            // @ts-ignore*/}
                {/*            id: updated.id,*/}
                {/*            // @ts-ignore*/}
                {/*            properties: updated.properties,*/}
                {/*        });*/}
                {/*        await fetchProjectData();*/}
                {/*        setFullProject(updated);*/}
                {/*    }}*/}
                <GovtConfig
                    surveys={surveys}
                    initialValue={safe_get(fullProject, "properties.gotv_config.value", null)}
                    onSave={async (config) => {
                        const updated = _.cloneDeep(fullProject);

                        const { voter_availibility, restrict_voter_info, ...gotvConfigValue } = config;

                        _.set(updated, "properties.gotv_config", {
                            id:
                                // @ts-ignore
                                updated.properties.gotv_config?.id || makeid_alpa_numeric(5),
                            type: "json",
                            value: gotvConfigValue,
                        });

                        if (voter_availibility) {
                            _.set(updated, "properties.voter_availibility", {
                                id:
                                    // @ts-ignore
                                    updated.properties.voter_availibility?.id || makeid_alpa_numeric(5),
                                type: "json",
                                value: voter_availibility,
                            });
                        }

                        if (restrict_voter_info) {
                            _.set(updated, "properties.restrict_voter_info", {
                                id:
                                    // @ts-ignore
                                    updated.properties.restrict_voter_info?.id || makeid_alpa_numeric(5),
                                type: "json",
                                value: restrict_voter_info,
                            });
                        }

                        await projectStore.editProjectProperties({
                            // @ts-ignore
                            id: updated.id,
                            // @ts-ignore
                            properties: updated.properties,
                        });

                        await fetchProjectData();
                        setFullProject(updated);
                    }}
                />
            </>
        );
    };

    const Banners = () => {
        const { isUploading } = useFileUpload();
        const [uploadingSize, setUploadingSize] = useState<"lg" | "md" | "sm" | "background" | null>(null);
        const [bannerEnabled, setBannerEnabled] = useState(false);
        const [bannerPaths, setBannerPaths] = useState({
            lg: "",
            md: "",
            sm: "",
        });
        const [backgroundImagePath, setBackgroundImagePath] = useState("");
        const backgroundImageInputRef = useRef<HTMLInputElement>(null);

        useEffect(() => {
            const webUxConfig = safe_get(fullProject, "properties.web_ux_config.value", {});
            const bannerConfig = safe_get(webUxConfig, "banner", {
                enabled: false,
                lg: "",
                md: "",
                sm: "",
            });

            setBannerEnabled(bannerConfig.enabled || false);
            setBannerPaths({
                lg: bannerConfig.lg || "",
                md: bannerConfig.md || "",
                sm: bannerConfig.sm || "",
            });
            setBackgroundImagePath(bannerConfig.background_image || "");
        }, [fullProject]);

        const saveBannerConfig = async (bannerData: {
            enabled: boolean;
            lg: string;
            md: string;
            sm: string;
            background_image: string;
        }) => {
            const updatedValues = _.cloneDeep(fullProject);

            // Ensure properties exist
            if (!updatedValues["properties"]) {
                updatedValues["properties"] = {};
            }

            // Ensure web_ux_config property exists
            if (!updatedValues["properties"]["web_ux_config"]) {
                updatedValues["properties"]["web_ux_config"] = {
                    id: makeid_alpa_numeric(5),
                    type: "json",
                    value: {},
                };
            }

            // Get existing web_ux_config or initialize
            const webUxConfig = updatedValues["properties"]["web_ux_config"]["value"] || {};
            webUxConfig.banner = bannerData;
            updatedValues["properties"]["web_ux_config"]["value"] = webUxConfig;

            try {
                const payload = {
                    id: updatedValues["id"],
                    properties: updatedValues["properties"],
                };

                setLoading(true);
                await projectStore.editProjectProperties(payload);

                // Invalidate react-query cache for getfullproject
                queryClient.invalidateQueries({
                    queryKey: ["full-project", projectid],
                });

                await fetchProjectData();
                setLoading(false);

                setFullProject({ ...updatedValues });
                return true;
            } catch (error) {
                console.error("Error saving banner config:", error);
                toast.error("Failed to save banner configuration");
                setLoading(false);
                return false;
            }
        };

        const handleFileUpload = async (size: "lg" | "md" | "sm", file: File) => {
            // Validate file type
            const validTypes = ["image/jpeg", "image/jpg", "image/png"];
            if (!validTypes.includes(file.type)) {
                toast.error("Please upload a JPG or PNG image");
                return;
            }

            // Get file extension
            const extension = file.name.split(".").pop()?.toLowerCase() || "jpg";
            // Generate random 4-character string for cache-busting
            const randomStr = makeid_alpa_numeric(4);
            const fileName = `p${projectid}_${size}_${randomStr}.${extension}`;
            const s3Path = `banners/${fileName}`;

            setUploadingSize(size);
            try {
                const result = await uploadBlob("banners", fileName, file, "urn-assets");

                if (result && result.key) {
                    // The key should be the full path: banners/p{projectid}_{size}_{random4}.{ext}
                    const fullPath = result.key;

                    // Update banner paths
                    const updatedPaths = { ...bannerPaths, [size]: fullPath };
                    setBannerPaths(updatedPaths);

                    // Save to web_ux_config
                    const success = await saveBannerConfig({
                        enabled: bannerEnabled,
                        lg: updatedPaths.lg,
                        md: updatedPaths.md,
                        sm: updatedPaths.sm,
                        background_image: backgroundImagePath,
                    });

                    if (success) {
                        toast.success(`${size.toUpperCase()} banner uploaded successfully`);
                    }
                }
            } catch (error) {
                console.error("Error uploading banner:", error);
                toast.error("Failed to upload banner");
            } finally {
                setUploadingSize(null);
            }
        };

        async function handleBackgroundImageUpload(file: File) {
            const validTypes = ["image/jpeg", "image/jpg", "image/png"];
            if (!validTypes.includes(file.type)) {
                toast.error("Please upload a JPG or PNG image");
                return;
            }

            const extension = file.name.split(".").pop()?.toLowerCase() || "jpg";
            const randomStr = makeid_alpa_numeric(4);
            const fileName = `p${projectid}_background_${randomStr}.${extension}`;

            setUploadingSize("background");
            try {
                const result = await uploadBlob("banners", fileName, file, "urn-assets");
                if (result && result.key) {
                    const backgroundImage = result.key;
                    setBackgroundImagePath(backgroundImage);
                    const success = await saveBannerConfig({
                        enabled: bannerEnabled,
                        lg: bannerPaths.lg,
                        md: bannerPaths.md,
                        sm: bannerPaths.sm,
                        background_image: backgroundImage,
                    });
                    if (success) {
                        toast.success("Dashboard background image uploaded successfully");
                    }
                }
            } catch (error) {
                console.error("Error uploading dashboard background image:", error);
                toast.error("Failed to upload dashboard background image");
            } finally {
                setUploadingSize(null);
            }
        }

        const handleRemoveBackgroundImage = async () => {
            const success = await saveBannerConfig({
                enabled: bannerEnabled,
                lg: bannerPaths.lg,
                md: bannerPaths.md,
                sm: bannerPaths.sm,
                background_image: "",
            });
            if (success) {
                setBackgroundImagePath("");
                toast.success("Dashboard background image removed");
            }
        };

        const handleToggleEnabled = async (enabled: boolean) => {
            setBannerEnabled(enabled);
            await saveBannerConfig({
                enabled,
                lg: bannerPaths.lg,
                md: bannerPaths.md,
                sm: bannerPaths.sm,
                background_image: backgroundImagePath,
            });
            toast.success(`Banners ${enabled ? "enabled" : "disabled"}`);
        };

        const BannerUploadSection = ({ size, label }: { size: "lg" | "md" | "sm"; label: string }) => {
            const fileInputRef = useRef<HTMLInputElement>(null);
            const [showFullPreview, setShowFullPreview] = useState(false);

            const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                const file = e.target.files?.[0];
                if (file) {
                    handleFileUpload(size, file);
                }
                // Reset input
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
            };

            return (
                <Card className="h-full shadow-md transition-shadow hover:shadow-lg">
                    <CardContent>
                        <Typography variant="h6" className="mb-4 font-bold text-gray-800">
                            {label} Banner
                            <span className="ml-2 text-sm font-normal text-gray-500">
                                ({size.toUpperCase()})
                            </span>
                        </Typography>

                        <div className="flex flex-col gap-4">
                            <div className="flex items-center gap-4">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/jpeg,image/jpg,image/png"
                                    onChange={handleFileChange}
                                    className="hidden"
                                    id={`banner-upload-${size}`}
                                />
                                <label htmlFor={`banner-upload-${size}`}>
                                    <Button
                                        variant="contained"
                                        component="span"
                                        disabled={uploadingSize !== null || !bannerEnabled}
                                        startIcon={<i className="fa-solid fa-cloud-upload-alt" />}
                                        color="primary"
                                        sx={{ textTransform: "none" }}
                                    >
                                        {uploadingSize === size
                                            ? "Uploading..."
                                            : bannerPaths[size]
                                              ? "Replace Banner"
                                              : "Upload Banner"}
                                    </Button>
                                </label>
                            </div>

                            {bannerPaths[size] && (
                                <div className="mt-4">
                                    <div className="mb-2 flex items-center justify-between">
                                        <Typography variant="caption" className="text-gray-500">
                                            S3 Path: {bannerPaths[size]}
                                        </Typography>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => setShowFullPreview(true)}
                                            sx={{ textTransform: "none" }}
                                        >
                                            View Full Size
                                        </Button>
                                    </div>
                                    <div
                                        className="mt-3 flex min-h-[200px] cursor-pointer items-center justify-center overflow-hidden rounded-lg border-2 border-gray-200 bg-gray-50 p-2 transition-colors hover:border-blue-300"
                                        onClick={() => setShowFullPreview(true)}
                                        title="Click to view full size"
                                    >
                                        <div className="flex h-full w-full items-center justify-center">
                                            <ThumbnailImage
                                                s3Key={bannerPaths[size]}
                                                bucket="urn-assets"
                                                width={800}
                                                height={600}
                                                fit="FIT"
                                                className="h-auto max-h-[400px] w-auto max-w-full rounded"
                                            />
                                        </div>
                                    </div>

                                    {/* Full Size Preview Modal */}
                                    <Dialog
                                        open={showFullPreview}
                                        onClose={() => setShowFullPreview(false)}
                                        maxWidth="lg"
                                        fullWidth
                                    >
                                        <DialogContent
                                            sx={{
                                                p: 2,
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                minHeight: "60vh",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    position: "relative",
                                                    width: "100%",
                                                    height: "100%",
                                                }}
                                            >
                                                <IconButton
                                                    onClick={() => setShowFullPreview(false)}
                                                    sx={{
                                                        position: "absolute",
                                                        top: 8,
                                                        right: 8,
                                                        zIndex: 1,
                                                        bgcolor: "rgba(255, 255, 255, 0.9)",
                                                        "&:hover": {
                                                            bgcolor: "rgba(255, 255, 255, 1)",
                                                        },
                                                    }}
                                                >
                                                    <i className="fa-solid fa-times" />
                                                </IconButton>
                                                <div className="flex h-full w-full items-center justify-center">
                                                    <ThumbnailImage
                                                        s3Key={bannerPaths[size]}
                                                        bucket="urn-assets"
                                                        width={1200}
                                                        height={800}
                                                        fit="FIT"
                                                        className="h-auto max-h-[80vh] w-auto max-w-full rounded shadow-lg"
                                                    />
                                                </div>
                                            </Box>
                                        </DialogContent>
                                    </Dialog>
                                </div>
                            )}

                            {!bannerPaths[size] && (
                                <Typography variant="body2" className="italic text-gray-400">
                                    No banner uploaded yet
                                </Typography>
                            )}
                        </div>
                    </CardContent>
                </Card>
            );
        };

        return (
            <div className="mt-4">
                <div className="mb-6">
                    <Typography variant="h4" className="mb-2 font-bold text-gray-800">
                        Manage Banners
                    </Typography>
                    <Typography variant="body2" className="mb-4 text-gray-600">
                        Upload banners for different screen sizes. Supported formats: JPG, PNG
                    </Typography>

                    <Card className="mb-6 shadow-sm">
                        <CardContent>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={bannerEnabled}
                                        onChange={(e) => handleToggleEnabled(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label={
                                    <Typography variant="h6" className="font-semibold">
                                        Enable Banners
                                    </Typography>
                                }
                            />
                            <Typography variant="caption" className="mt-2 block text-gray-500">
                                {bannerEnabled
                                    ? "Banners are currently enabled and will be displayed"
                                    : "Banners are disabled and will not be displayed"}
                            </Typography>
                        </CardContent>
                    </Card>
                    <Card className="mb-6 shadow-sm">
                        <CardContent>
                            <Typography variant="h6" className="mb-3 font-semibold text-gray-800">
                                Dashboard Background Image
                            </Typography>
                            <input
                                ref={backgroundImageInputRef}
                                type="file"
                                accept="image/jpeg,image/jpg,image/png"
                                className="hidden"
                                onChange={(event) => {
                                    const file = event.target.files?.[0];
                                    if (file) handleBackgroundImageUpload(file);
                                    event.target.value = "";
                                }}
                            />
                            <div className="flex flex-wrap gap-2">
                                <Button
                                    variant="contained"
                                    onClick={() => backgroundImageInputRef.current?.click()}
                                    disabled={uploadingSize !== null}
                                    startIcon={<i className="fa-solid fa-cloud-upload-alt" />}
                                    sx={{ textTransform: "none" }}
                                >
                                    {uploadingSize === "background"
                                        ? "Uploading..."
                                        : backgroundImagePath
                                          ? "Replace Background Image"
                                          : "Upload Background Image"}
                                </Button>
                                {backgroundImagePath && (
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={handleRemoveBackgroundImage}
                                        disabled={uploadingSize !== null}
                                        startIcon={<DeleteIcon />}
                                        sx={{ textTransform: "none" }}
                                    >
                                        Remove Background Image
                                    </Button>
                                )}
                            </div>
                            {backgroundImagePath && (
                                <div className="mt-4">
                                    <Typography variant="caption" className="mb-2 block text-gray-500">
                                        S3 Path: {backgroundImagePath}
                                    </Typography>
                                    <div className="flex h-40 w-64 max-w-full items-center justify-center overflow-hidden rounded border border-gray-200 bg-gray-50 p-2">
                                        <ThumbnailImage
                                            s3Key={backgroundImagePath}
                                            bucket="urn-assets"
                                            width={1200}
                                            height={500}
                                            fit="FIT"
                                            className="max-h-full max-w-full object-contain"
                                        />
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <BannerUploadSection size="lg" label="Large" />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <BannerUploadSection size="md" label="Medium" />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <BannerUploadSection size="sm" label="Small" />
                    </Grid>
                </Grid>
            </div>
        );
    };

    if (loading) return <LoadingState />;

    const headerActions = <></>;

    // Main return statement of the component, rendering the breadcrumbs, project header, and content.
    return (
        <>
            <ProjectHeader />
            <PageShell
                title={"Manage Project"}
                headerActions={headerActions}
                breadcrumbs={<FTPBreadCrumbs crumbs={crumbs} />}
                contentPadding="px-4"
            >
                {/*TODO: Given permission for only Muralidhar (Asked by Srinivas sir)*/}
                {/*TODO: Given permission for only Akash (Asked by Vinay sir)*/}
                {userStore.isRoot() || userStore.user.id === 16003 || userStore.user.id === 22274 ? (
                    <div className="px-4">
                        <TabView />
                        <Routes>
                            <Route
                                path="/surveys"
                                element={
                                    fetched ? (
                                        <>
                                            {SurveyEditorLinks()}
                                            <ModalDialog />
                                        </>
                                    ) : null
                                }
                            />
                            <Route path="/properties" element={fetched ? RenderProperties() : null} />
                            <Route path="/web-features" element={fetched ? <WebFeatures /> : null} />
                            <Route
                                path="/profile-questions"
                                element={fetched ? <ProfileQuestions /> : null}
                            />
                            {leader && (
                                <Route
                                    path="/influential-leaders"
                                    element={fetched ? <InfluentialLeaders /> : null}
                                />
                            )}
                            <Route path="/voter-segmentation-config" element={fetched ? <Gotv /> : null} />
                            <Route
                                path="/web-survey-links"
                                element={fetched ? <SurveyWebLinkManagement /> : null}
                            />
                            <Route path="/banners" element={fetched ? <Banners /> : null} />
                            <Route path="/webuxconfig" element={fetched ? <WebUxConfigBuilder /> : null} />
                            {/*<Route*/}
                            {/*    path="/analytical"*/}
                            {/*    element={fetched ? <AnalyticalSummary /> : null}*/}
                            {/*/>*/}
                            <Route
                                path="/analytical"
                                element={
                                    fetched ? (
                                        <AnalyticalSummary
                                            project={fullProject}
                                            surveys={surveys}
                                            onSave={async (summaryConfig) => {
                                                const updated = _.cloneDeep(fullProject);
                                                // _.set(
                                                //     updated,
                                                //     "properties.analytical_report_fields.value",
                                                //     summaryConfig
                                                // );

                                                _.set(updated, "properties.analytical_report_fields_v2", {
                                                    id:
                                                        // @ts-ignore
                                                        updated.properties.analytical_report_fields_v2?.id ||
                                                        makeid_alpa_numeric(5),
                                                    type: "json",
                                                    value: summaryConfig,
                                                });

                                                // @ts-ignore
                                                const payload = {
                                                    // @ts-ignore
                                                    id: updated.id,
                                                    properties:
                                                        // @ts-ignore
                                                        updated.properties,
                                                };

                                                setLoading(true);
                                                await projectStore.editProjectProperties(payload);
                                                await fetchProjectData();
                                                setLoading(false);

                                                setFullProject(updated);
                                                toast.success("Analytical Summary saved");
                                            }}
                                        />
                                    ) : null
                                }
                            />

                            <Route
                                path="/"
                                element={
                                    fetched ? (
                                        <>
                                            {SurveyEditorLinks()}
                                            <ModalDialog />
                                        </>
                                    ) : null
                                }
                            />
                            <Route
                                path="/audio"
                                element={
                                    fetched ? (
                                        <AudioUpload
                                            onSubmit={(audio) => {
                                                console.log(audio);
                                            }}
                                        />
                                    ) : null
                                }
                            />
                        </Routes>
                    </div>
                ) : (
                    <Typography variant={"h6"} color={"error"} className={"p-4"}>
                        Permission Denied
                    </Typography>
                )}

                <DeleteModal
                    isOpen={deleteContext.open}
                    desc={deleteContext.description}
                    onDeleteClick={async () => {
                        await deleteContext.onConfirm?.();
                    }}
                    onCancelClick={closeDeleteModal}
                />
            </PageShell>
            {/* <h3 className="px-4 title">{t("manage_project")}</h3> */}
        </>
    );
});

export default ManageProject;
